import { useState, DragEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'

type Pipeline = { id: number; name: string; is_default: boolean; is_active: boolean }

type KanbanOpp = {
  id: number
  name: string
  account_id: number
  account_name: string
  amount: string
  currency_code: string
  probability: string
  weighted_amount: string
  expected_close_date: string | null
  owner_username: string | null
  status: 'OPEN' | 'WON' | 'LOST'
}

type Column = {
  stage_id: number
  stage_name: string
  stage_color: string
  probability: string
  is_won: boolean
  is_lost: boolean
  sort_order: number
  count: number
  total_amount: string
  weighted_amount: string
  opportunities: KanbanOpp[]
}

type KanbanData = {
  pipeline_id: number
  pipeline_name: string
  columns: Column[]
  forecast_weighted: string
  open_pipeline_total: string
}

export default function CRMKanban() {
  const qc = useQueryClient()
  const [pipelineId, setPipelineId] = useState<number | null>(null)
  const [draggingId, setDraggingId] = useState<number | null>(null)
  const [dragOverStage, setDragOverStage] = useState<number | null>(null)

  const pipelinesQ = useQuery({
    queryKey: ['crm', 'pipelines'],
    queryFn: async () => (await api.get<Pipeline[]>('/crm/pipelines/')).data,
  })

  const kanbanQ = useQuery({
    queryKey: ['crm', 'kanban', pipelineId],
    queryFn: async () => {
      const params: any = {}
      if (pipelineId) params.pipeline = pipelineId
      return (await api.get<KanbanData>('/crm/kanban/', { params })).data
    },
    enabled: !!(pipelinesQ.data && pipelinesQ.data.length > 0),
  })

  const moveMut = useMutation({
    mutationFn: async ({ opp_id, stage_id }: { opp_id: number; stage_id: number }) =>
      (await api.post(`/crm/opportunities/${opp_id}/move/`, { stage_id })).data,
    onMutate: async ({ opp_id, stage_id }) => {
      await qc.cancelQueries({ queryKey: ['crm', 'kanban'] })
      const previous = qc.getQueryData<KanbanData>(['crm', 'kanban', pipelineId])
      if (previous) {
        // Optimistic move
        const updated: KanbanData = JSON.parse(JSON.stringify(previous))
        let moved: KanbanOpp | null = null
        for (const col of updated.columns) {
          const idx = col.opportunities.findIndex((o) => o.id === opp_id)
          if (idx >= 0) {
            moved = col.opportunities[idx]
            col.opportunities.splice(idx, 1)
            col.count -= 1
            col.total_amount = String(Number(col.total_amount) - Number(moved.amount))
            col.weighted_amount = String(Number(col.weighted_amount) - Number(moved.weighted_amount))
            break
          }
        }
        if (moved) {
          const targetCol = updated.columns.find((c) => c.stage_id === stage_id)
          if (targetCol) {
            targetCol.opportunities.push(moved)
            targetCol.count += 1
            targetCol.total_amount = String(Number(targetCol.total_amount) + Number(moved.amount))
            targetCol.weighted_amount = String(Number(targetCol.weighted_amount) + Number(moved.weighted_amount))
          }
        }
        qc.setQueryData(['crm', 'kanban', pipelineId], updated)
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['crm', 'kanban', pipelineId], ctx.previous)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'kanban'] })
      qc.invalidateQueries({ queryKey: ['crm', 'opportunities'] })
    },
  })

  const pipelines = (pipelinesQ.data || []).filter((p) => p.is_active)
  const data = kanbanQ.data

  const handleDragStart = (e: DragEvent<HTMLDivElement>, oppId: number) => {
    setDraggingId(oppId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(oppId))
  }
  const handleDragEnd = () => {
    setDraggingId(null)
    setDragOverStage(null)
  }
  const handleDragOver = (e: DragEvent<HTMLDivElement>, stageId: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverStage !== stageId) setDragOverStage(stageId)
  }
  const handleDrop = (e: DragEvent<HTMLDivElement>, stageId: number) => {
    e.preventDefault()
    setDragOverStage(null)
    const oppId = Number(e.dataTransfer.getData('text/plain') || draggingId)
    setDraggingId(null)
    if (!oppId) return
    // Find current stage
    const currentStage = data?.columns.find((c) => c.opportunities.find((o) => o.id === oppId))
    if (currentStage && currentStage.stage_id === stageId) return
    moveMut.mutate({ opp_id: oppId, stage_id: stageId })
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Pipeline (Kanban)</h2>
          <p className="text-sm text-gray-500">Drag deals between stages.</p>
        </div>
        <div className="flex items-center space-x-3">
          {pipelines.length > 1 && (
            <select value={pipelineId || ''}
              onChange={(e) => setPipelineId(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm">
              <option value="">Default pipeline</option>
              {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard label="Pipeline" value={data.pipeline_name} />
          <StatCard label="Open deals" value={data.columns.filter((c) => !c.is_won && !c.is_lost).reduce((s, c) => s + c.count, 0).toString()} />
          <StatCard label="Open value" value={Number(data.open_pipeline_total).toFixed(2)} />
          <StatCard label="Forecast (weighted)" value={Number(data.forecast_weighted).toFixed(2)} highlight />
        </div>
      )}

      {kanbanQ.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : !data ? (
        <p className="text-gray-500">No pipeline data.</p>
      ) : (
        <div className="overflow-x-auto pb-3">
          <div className="flex space-x-3 min-w-max">
            {data.columns.map((col) => (
              <div
                key={col.stage_id}
                onDragOver={(e) => handleDragOver(e, col.stage_id)}
                onDrop={(e) => handleDrop(e, col.stage_id)}
                className={`w-72 shrink-0 bg-gray-100 rounded-lg ${
                  dragOverStage === col.stage_id ? 'ring-2 ring-blue-400 bg-blue-50' : ''
                }`}
              >
                <div className="px-3 py-2 border-b border-gray-200 sticky top-0 bg-gray-100 rounded-t-lg"
                  style={{ borderTopColor: col.stage_color || '#94a3b8', borderTopWidth: 3 }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-sm text-gray-800">{col.stage_name}</h3>
                      <p className="text-xs text-gray-500">
                        {col.count} · {Number(col.total_amount).toFixed(0)}
                      </p>
                    </div>
                    {col.is_won && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">WON</span>}
                    {col.is_lost && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">LOST</span>}
                    {!col.is_won && !col.is_lost && (
                      <span className="text-xs text-gray-500">{Number(col.probability).toFixed(0)}%</span>
                    )}
                  </div>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {col.opportunities.length === 0 ? (
                    <p className="text-xs text-gray-400 italic text-center py-4">Drop deals here</p>
                  ) : (
                    col.opportunities.map((o) => (
                      <div
                        key={o.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, o.id)}
                        onDragEnd={handleDragEnd}
                        className={`bg-white border border-gray-200 rounded p-3 shadow-sm cursor-move hover:shadow-md transition ${
                          draggingId === o.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="font-medium text-sm text-gray-800 truncate">{o.name}</div>
                        <div className="text-xs text-gray-500 truncate">{o.account_name}</div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-mono text-sm text-gray-700">
                            {o.currency_code} {Number(o.amount).toFixed(0)}
                          </span>
                          <span className="text-xs text-gray-400">{Number(o.probability).toFixed(0)}%</span>
                        </div>
                        {o.expected_close_date && (
                          <div className="text-xs text-gray-400 mt-1">{o.expected_close_date}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {moveMut.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mt-4">
          Move failed — change reverted.
        </div>
      )}
    </AdminLayout>
  )
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`bg-white rounded-lg shadow p-3 ${highlight ? 'border-l-4 border-blue-500' : ''}`}>
      <div className="text-xs uppercase text-gray-500 tracking-wide">{label}</div>
      <div className="text-lg font-mono text-gray-800 mt-1">{value}</div>
    </div>
  )
}
